import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImagePlus, Loader2 } from "lucide-react";
import { api, uploadImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const emptyFromFields = (fields) =>
  fields.reduce((acc, f) => {
    acc[f.name] = f.type === "switch" ? true : f.type === "number" ? 0 : "";
    return acc;
  }, {});

/**
 * Generic list + create/edit/delete UI for a REST resource.
 * queryKey must match the key used by the matching public useCollection() call
 * so admin mutations invalidate and refresh the public landing page too.
 */
export const ResourceManager = ({
  title,
  description,
  queryKey,
  endpoint,
  fields,
  columns,
  extraInvalidateKeys = [],
}) => {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({
    queryKey: [queryKey],
    queryFn: async () => (await api.get(endpoint)).data,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyFromFields(fields));
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyFromFields(fields));
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    const next = {};
    fields.forEach((f) => (next[f.name] = item[f.name] ?? emptyFromFields(fields)[f.name]));
    setForm(next);
    setDialogOpen(true);
  };

  const update = (name, value) =>
    setForm((f) => {
      const next = { ...f, [name]: value };
      const field = fields.find((x) => x.name === name);
      if (field?.onChangeExtra) Object.assign(next, field.onChangeExtra(value, f));
      return next;
    });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: [queryKey] });
    extraInvalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const handleUpload = async (name, file) => {
    if (!file) return;
    setUploadingField(name);
    try {
      const url = await uploadImage(file);
      update(name, url);
    } catch (err) {
      toast.error("Gagal mengunggah gambar.");
    } finally {
      setUploadingField(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      payload[f.name] = f.type === "number" ? Number(form[f.name]) || 0 : form[f.name];
    });
    try {
      if (editingId) {
        await api.put(`${endpoint}/${editingId}`, payload);
        toast.success("Perubahan disimpan.");
      } else {
        await api.post(endpoint, payload);
        toast.success("Data baru ditambahkan.");
      }
      invalidateAll();
      setDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan data.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${endpoint}/${deleteTarget.id}`);
      toast.success("Data dihapus.");
      invalidateAll();
    } catch (err) {
      toast.error("Gagal menghapus data.");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">{title}</h1>
          {description && <p className="text-stone2 text-sm mt-1">{description}</p>}
        </div>
        <Button
          onClick={openCreate}
          data-testid={`${queryKey}-add-btn`}
          className="rounded-full bg-honey hover:bg-honey-dark text-cream font-semibold gap-2"
        >
          <Plus size={16} />
          Tambah
        </Button>
      </div>

      <div className="rounded-2xl bg-white border border-border/60 overflow-x-auto">
        {isLoading ? (
          <div className="p-10 text-center text-stone2">Memuat...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-stone2">Belum ada data.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => (
                  <TableHead key={c.key}>{c.label}</TableHead>
                ))}
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid={`${queryKey}-row-${item.id}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key}>
                      {c.render ? c.render(item) : item[c.key]}
                    </TableCell>
                  ))}
                  <TableCell className="text-right whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(item)}
                      data-testid={`${queryKey}-edit-${item.id}`}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget(item)}
                      data-testid={`${queryKey}-delete-${item.id}`}
                    >
                      <Trash2 size={16} className="text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Data" : "Tambah Data"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map((f) => (
              <div key={f.name}>
                <Label className="mb-2 block">{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea
                    value={form[f.name] ?? ""}
                    onChange={(e) => update(f.name, e.target.value)}
                    rows={f.rows || 4}
                  />
                ) : f.type === "select" ? (
                  <Select value={form[f.name] ?? ""} onValueChange={(v) => update(f.name, v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "switch" ? (
                  <Switch checked={!!form[f.name]} onCheckedChange={(v) => update(f.name, v)} />
                ) : f.type === "image" ? (
                  <div className="flex items-center gap-3">
                    {form[f.name] && (
                      <img
                        src={form[f.name]}
                        alt=""
                        className="h-16 w-16 rounded-lg object-cover border border-border/60"
                      />
                    )}
                    <Input
                      value={form[f.name] ?? ""}
                      onChange={(e) => update(f.name, e.target.value)}
                      placeholder="URL gambar, atau unggah file"
                      className="flex-1"
                    />
                    <label className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-input cursor-pointer hover:bg-accent shrink-0">
                      {uploadingField === f.name ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <ImagePlus size={16} />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleUpload(f.name, e.target.files?.[0])}
                      />
                    </label>
                  </div>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    step={f.step}
                    value={form[f.name] ?? ""}
                    onChange={(e) => update(f.name, e.target.value)}
                  />
                )}
              </div>
            ))}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Batal
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid={`${queryKey}-save-btn`}
                className="bg-honey hover:bg-honey-dark text-cream"
              >
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus data ini?</AlertDialogTitle>
            <AlertDialogDescription>
              Tindakan ini tidak dapat dibatalkan dan akan langsung tampil di situs publik.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
